/**
 * exemplar-quality-core — the SINGLE SOURCE for the static, block-only design-quality signals
 * used by the exemplar corpus quality FLOOR.
 *
 * Lives in cogo-kg-browser (the lower package) so BOTH the bulk corpus miner (mine-g-examples,
 * in this package) and cogo-headless (exemplar-quality-floor) share one definition with NO
 * dependency cycle and NO drift. Pure: no I/O, no deps.
 *
 * Foundational + forward-looking: the corpus is grown by the Stitch/Refero ingestion pipeline,
 * whose job is exactly TOKENIZATION (raw hex -> semantic tokens) and shadcn standardization. So the
 * floor enforces that standard — tokenized (no raw style) + built from the FULL ontology semantic
 * vocabulary (parsed from semantic-types-v1.ttl, NOT a hardcoded subset). Legacy un-tokenized
 * blocks failing the floor is correct; they must be re-ingested through Stitch to qualify.
 */

/** Raw-style leakage signals: a corpus block must not embed raw hex / rgba / arbitrary `x-[..]` / glow. */
export const RAW_STYLE_PATTERNS: RegExp[] = [
  /#[0-9a-fA-F]{3,8}/,
  /rgba?\(/,
  /\b[a-z][a-z0-9-]*-\[[^\]]+\]/,
  /backdrop-blur-xl/,
  /shadow-glow/,
]

/**
 * Tailwind utility prefixes whose arbitrary `-[..]` value is a legitimate LAYOUT template with no
 * token equivalent (a `1fr_2fr` grid, a `16/9` ratio) — these are NOT raw-style leaks.
 */
const LAYOUT_TEMPLATE_PREFIXES = new Set([
  "grid-cols", "grid-rows", "col", "row", "aspect", "grid", "order", "auto-cols", "auto-rows",
])

/**
 * Count GENUINE raw-style leaks in a block's serialized text. PRECISE, not over-broad:
 *  - literal hex / rgba color and the poison markers (shadow-glow / backdrop-blur-xl) — always leaks;
 *  - an arbitrary `<prefix>-[..]` utility is a leak ONLY when the bracket belongs to the BASE utility
 *    (i.e. is NOT immediately followed by `:`, which marks a data-/has-/aria-/group-/peer-/supports-/
 *    min-/max- VARIANT SELECTOR) AND the prefix is not a layout template (grid-cols/aspect/…).
 *
 * This is the root-cause fix for over-dropping: legitimate shadcn state variants
 * (`has-data-[state=open]:bg-card`) and responsive grid templates (`grid-cols-[1fr_2fr]`) were being
 * miscounted as un-tokenized raw style. Genuine raw color (`bg-[#6366f1]`, `text-[#fff]`) and off-grid
 * sizing (`w-[200px]`) still count — those are exactly what the Stitch tokenizer resolves.
 */
export function countRawStyleLeaks(text: string): number {
  let leaks = 0
  if (/#[0-9a-fA-F]{3,8}\b/.test(text)) leaks++
  if (/rgba?\(/.test(text)) leaks++
  if (/\bshadow-glow\b/.test(text)) leaks++
  if (/\bbackdrop-blur-xl\b/.test(text)) leaks++
  const re = /([a-z][a-z0-9-]*)-\[[^\]]*\](:)?/g
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    if (m[2] === ":") continue // variant selector (data-/has-/aria-/group-/peer-/supports-/min-/max-)
    if (LAYOUT_TEMPLATE_PREFIXES.has(m[1])) continue // legitimate layout template
    leaks++
  }
  return leaks
}

/**
 * Parse the canonical semantic-component vocabulary from semantic-types-v1.ttl content
 * (`ctype:<name> a cogo:SemanticType`). This is the SINGLE SOURCE — the floor recognizes exactly
 * the types the ontology defines (and that Stitch/Refero target), not a hardcoded subset.
 */
export function parseSemanticTypesFromTtl(ttl: string): Set<string> {
  const out = new Set<string>()
  const re = /\bctype:([A-Za-z0-9_-]+)\s+a\s+cogo:SemanticType\b/g
  let m: RegExpExecArray | null
  while ((m = re.exec(ttl)) !== null) out.add(m[1])
  return out
}

/** Minimal fallback when no ontology vocabulary is injected (real callers inject the full set). */
export const FALLBACK_SEMANTIC_TYPES: Set<string> = new Set([
  "button", "input", "checkbox", "select", "textarea", "switch", "radio", "slider", "form",
  "text", "container", "card", "list", "table", "data-table", "chart-line",
])

export interface ExemplarQualityFloorThresholds {
  /** minimum static score (0-100). */
  minScore: number
  /** maximum tolerated raw-style leaks (default 0). */
  maxRawStyleLeaks: number
  /** maximum tolerated author-authored `style` objects in UI JSON (default 0). */
  maxAuthorStyleObjectLeaks: number
  /** minimum ratio of typed nodes that are known semantic components. */
  minSemanticRatio: number
}

export const DEFAULT_EXEMPLAR_QUALITY_FLOOR: ExemplarQualityFloorThresholds = {
  minScore: 80,
  maxRawStyleLeaks: 0,
  maxAuthorStyleObjectLeaks: 0,
  minSemanticRatio: 0.6,
}

export interface ExemplarStaticQualityCore {
  passed: boolean
  score: number
  reasons: string[]
  rawStyleLeaks: number
  authorStyleObjectLeaks: number
  semanticRatio: number
  nodeCount: number
}

export interface AssessExemplarCoreOptions {
  /** the ontology semantic vocabulary (inject from semantic-types-v1.ttl). Falls back to FALLBACK_SEMANTIC_TYPES. */
  semanticTypes?: Set<string>
  thresholds?: Partial<ExemplarQualityFloorThresholds>
}

interface UiNode {
  type?: unknown
  children?: unknown
}

function collectNodes(root: unknown, acc: UiNode[] = []): UiNode[] {
  if (!root || typeof root !== "object") return acc
  const node = root as UiNode
  if (typeof node.type === "string") acc.push(node)
  if (Array.isArray(node.children)) for (const child of node.children) collectNodes(child, acc)
  return acc
}

function countAuthorStyleObjectLeaks(root: unknown): number {
  if (!root || typeof root !== "object") return 0
  const node = root as Record<string, unknown>
  let leaks = 0
  if (
    Object.prototype.hasOwnProperty.call(node, "style") &&
    node.style &&
    typeof node.style === "object" &&
    !Array.isArray(node.style)
  ) {
    leaks++
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) leaks += countAuthorStyleObjectLeaks(child)
  }
  return leaks
}

/**
 * Measure a block's static design quality (leak / semantic-ratio / structural) against the FULL
 * ontology vocabulary and decide whether it clears the floor. Fail-closed. Conformance (SHACL) is
 * layered on top in cogo-headless.
 */
export function assessExemplarStaticQualityCore(
  blockJson: { ui?: { root?: unknown } } | Record<string, unknown>,
  options: AssessExemplarCoreOptions = {},
): ExemplarStaticQualityCore {
  const t = { ...DEFAULT_EXEMPLAR_QUALITY_FLOOR, ...options.thresholds }
  const semanticTypes = options.semanticTypes ?? FALLBACK_SEMANTIC_TYPES
  const ui = (blockJson as { ui?: { root?: unknown } }).ui
  const nodes = collectNodes(ui?.root)

  if (nodes.length === 0) {
    return {
      passed: false,
      score: 0,
      reasons: ["no-typed-ui-nodes"],
      rawStyleLeaks: 0,
      authorStyleObjectLeaks: 0,
      semanticRatio: 0,
      nodeCount: 0,
    }
  }

  const reasons: string[] = []
  const text = JSON.stringify(blockJson)
  const rawStyleLeaks = countRawStyleLeaks(text)
  if (rawStyleLeaks > t.maxRawStyleLeaks) reasons.push(`raw-style-leak:${rawStyleLeaks}`)

  const authorStyleObjectLeaks = countAuthorStyleObjectLeaks(ui?.root)
  if (authorStyleObjectLeaks > t.maxAuthorStyleObjectLeaks) {
    reasons.push(`author-style-object-leak:${authorStyleObjectLeaks}`)
  }

  const semantic = nodes.filter((n) => typeof n.type === "string" && semanticTypes.has(n.type)).length
  const semanticRatio = semantic / nodes.length
  if (semanticRatio < t.minSemanticRatio) reasons.push(`low-semantic-ratio:${semanticRatio.toFixed(2)}`)

  const styleAuthorityPassed = rawStyleLeaks <= t.maxRawStyleLeaks && authorStyleObjectLeaks <= t.maxAuthorStyleObjectLeaks
  const score = (styleAuthorityPassed ? 50 : 0) + Math.round(50 * Math.min(1, semanticRatio))
  if (score < t.minScore) reasons.push(`score-below-floor:${score}`)

  const passed = styleAuthorityPassed && semanticRatio >= t.minSemanticRatio && score >= t.minScore
  return { passed, score, reasons, rawStyleLeaks, authorStyleObjectLeaks, semanticRatio, nodeCount: nodes.length }
}
